import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Box, Flex, Text } from "@radix-ui/themes";
import { BarChart3 } from "lucide-react";
import { ChartConfiguration } from "@/types/breeze/ChartConfiguration";
import { PaginatedResult } from "@/types/breeze/Any";
import { formatAxisValue, makeIntervalFormatter } from "./chartFormat";

interface ChartRendererProps {
  chart: ChartConfiguration;
  entityData: PaginatedResult<Record<string, unknown>> | null;
  compareItems?: Record<string, unknown>[] | null;
  height?: string | number;
  showNoData?: boolean;
}

const COMPARE_SUFFIX = "__compare";
const COMPARE_COLOR = "#f97316";

export default function ChartRenderer({
  chart,
  entityData,
  compareItems,
  height = "400px",
  showNoData = true,
}: ChartRendererProps) {
  const xKey = chart.xField ?? "";

  // Process data for chart
  const chartData = useMemo(() => {
    if (!entityData?.items || !xKey) {
      return [];
    }

    // For pie charts, we need to aggregate data
    if (chart.chartType === "pie") {
      // Server has already aggregated (chart.series set) — take one row per period.
      const preAggregated = !!(chart.series && chart.series.length > 0);
      const aggregated: Record<string, number> = {};

      if (preAggregated) {
        entityData.items.forEach((item) => {
          const key = String(item[xKey] ?? "Unknown");
          aggregated[key] = Number(item.value ?? 0);
        });
      } else if (chart.pieMode === "count") {
        entityData.items.forEach((item) => {
          const key = String(item[xKey] || "Unknown");
          aggregated[key] = (aggregated[key] || 0) + 1;
        });
      } else {
        if (!chart.yField) return [];
        entityData.items.forEach((item) => {
          const key = String(item[xKey] || "Unknown");
          const value = Number(item[chart.yField!]) || 0;
          aggregated[key] = (aggregated[key] || 0) + value;
        });
      }

      return Object.entries(aggregated).map(([key, value]) => ({
        name: key,
        value: value,
      }));
    }

    // Stacked rows already carry one numeric column per series — pass through.
    if (chart.series && chart.series.length > 0) {
      return entityData.items as Record<string, unknown>[];
    }

    const primaryKey = chart.yField || "count";
    const primaryRows = entityData.items.map((item) => ({
      [xKey]: item[xKey],
      [primaryKey]: Number(item[primaryKey]) || 0,
    }));

    if (!compareItems || compareItems.length === 0) return primaryRows;

    const compareByPeriod = new Map<string, number>();
    for (const item of compareItems) {
      const period = String(item[xKey] ?? "");
      if (period) compareByPeriod.set(period, Number(item[primaryKey] ?? item.value ?? 0));
    }
    return primaryRows.map((row) => ({
      ...row,
      [`${primaryKey}${COMPARE_SUFFIX}`]: compareByPeriod.get(String(row[xKey])) ?? 0,
    }));
  }, [entityData, xKey, chart.yField, chart.chartType, chart.pieMode, chart.series, compareItems]);

  // Colors for charts
  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884d8",
    "#82ca9d",
  ];

  // chart.series carries the stacked categories from the server; fall back to yField for single-series charts.
  const seriesKeys = (chart.series && chart.series.length > 0)
    ? chart.series
    : [chart.yField || "count"];
  const hasCompare = !!(compareItems && compareItems.length > 0);
  const compareSeriesKeys = hasCompare ? seriesKeys.map((k) => `${k}${COMPARE_SUFFIX}`) : [];
  const isStacked = !!chart.stackBy && (chart.series?.length ?? 0) > 1;
  const legendFormatter = (value: unknown) =>
    String(value ?? "").replace(COMPARE_SUFFIX, " (prior)");
  const tooltipFormatter = (value: unknown, name: unknown) =>
    [formatAxisValue(value), String(name ?? "").replace(COMPARE_SUFFIX, " (prior)")];
  const xFormatter = useMemo(() => makeIntervalFormatter(chart.xInterval), [chart.xInterval]);

  if (!chartData.length && showNoData) {
    return (
      <Flex align="center" justify="center" style={{ height }}>
        <Box style={{ textAlign: "center", maxWidth: 320 }}>
          <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <Text size="3" weight="medium" as="div">
            Nothing to show yet
          </Text>
          <Text size="2" color="gray" as="div">
            No results for this configuration. Try widening the timeframe or removing filters.
          </Text>
        </Box>
      </Flex>
    );
  }

  const renderChart = () => {
    switch (chart.chartType) {
      case "pie":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value, percent }) => {
                  const formattedValue = typeof value === "number"
                    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : String(value);
                  return `${formatAxisValue(name)}: ${formattedValue} (${((percent || 0) * 100).toFixed(0)}%)`;
                }}
                outerRadius="80%"
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case "line":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey} tickFormatter={xFormatter} />
              <YAxis />
              <Tooltip labelFormatter={xFormatter} formatter={tooltipFormatter} />
              <Legend formatter={legendFormatter} />
              {seriesKeys.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COLORS[i % COLORS.length]}
                  strokeWidth={2}
                />
              ))}
              {compareSeriesKeys.map((key) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COMPARE_COLOR}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey} tickFormatter={xFormatter} />
              <YAxis />
              <Tooltip labelFormatter={xFormatter} formatter={tooltipFormatter} />
              <Legend formatter={legendFormatter} />
              {seriesKeys.map((key, i) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stackId={isStacked ? "stack" : undefined}
                  stroke={COLORS[i % COLORS.length]}
                  fill={COLORS[i % COLORS.length]}
                  fillOpacity={0.3}
                />
              ))}
              {compareSeriesKeys.map((key) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={COMPARE_COLOR}
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  fill="none"
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      default: // column
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xKey} tickFormatter={xFormatter} />
              <YAxis />
              <Tooltip labelFormatter={xFormatter} formatter={tooltipFormatter} />
              <Legend formatter={legendFormatter} />
              {seriesKeys.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId={isStacked ? "stack" : undefined}
                  fill={COLORS[i % COLORS.length]}
                />
              ))}
              {compareSeriesKeys.map((key) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={COMPARE_COLOR}
                  fillOpacity={0.6}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  const filterSummary = describeFilters(chart);
  const axisSubtitle = describeAxis(chart);

  return (
    <Flex direction="column" style={{ height, width: "100%" }} gap="1">
      {(filterSummary || axisSubtitle) && (
        <Flex direction="column" gap="0">
          {axisSubtitle && (
            <Text size="1" color="gray">{axisSubtitle}</Text>
          )}
          {filterSummary && (
            <Text size="1" color="gray">Filtered by: {filterSummary}</Text>
          )}
        </Flex>
      )}
      <Box style={{ flex: 1, minHeight: 0 }}>{renderChart()}</Box>
    </Flex>
  );
}

function describeAxis(chart: ChartConfiguration): string | null {
  if (chart.chartType === "stat" || chart.chartType === "gauge") return null;
  if (chart.chartType === "pie") {
    return chart.stackBy || chart.xField ? `Grouped by ${chart.stackBy || chart.xField}` : null;
  }
  const parts: string[] = [];
  if (chart.xField) parts.push(`X: ${chart.xField}`);
  if (chart.xInterval) parts.push(`per ${chart.xInterval}`);
  if (chart.stackBy) parts.push(`stacked by ${chart.stackBy}`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function describeFilters(chart: ChartConfiguration): string | null {
  const filters = chart.filters;
  if (!filters || filters.length === 0) return null;
  return filters
    .filter((f) => f.field && f.op)
    .map((f) => {
      switch (f.op) {
        case "exists": return `${f.field} exists`;
        case "not_exists": return `${f.field} does not exist`;
        case "eq": return `${f.field} = ${f.value ?? ""}`;
        case "ne": return `${f.field} ≠ ${f.value ?? ""}`;
        case "gt": return `${f.field} > ${f.value ?? ""}`;
        case "gte": return `${f.field} ≥ ${f.value ?? ""}`;
        case "lt": return `${f.field} < ${f.value ?? ""}`;
        case "lte": return `${f.field} ≤ ${f.value ?? ""}`;
        case "contains": return `${f.field} contains "${f.value ?? ""}"`;
        case "in": return `${f.field} in [${f.value ?? ""}]`;
        default: return `${f.field} ${f.op} ${f.value ?? ""}`;
      }
    })
    .join(" AND ");
}
